import axiosInstance from '../../../axiosInstance';
import apiEndpoints from '../../../api';
import { notify } from '../../../../utils/notification';

const clean = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

export const getComplaintsListReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_COMPLAINTS_LIST_API;
    const params = clean({
        society_id: action.data?.societyId,
        page: action.data?.page || 1,
        limit: action.data?.limit || 10,
        search: action.data?.search,
        status: action.data?.status,
        category: action.data?.category,
        priority: action.data?.priority,
        assigned_to_user_id: action.data?.assignedToUserId,
        raised_by_user_id: action.data?.raisedByUserId,
        flat_id: action.data?.flatId,
        from_date: action.data?.fromDate,
        to_date: action.data?.toDate,
    });
    return axiosInstance.get(url, { params })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load complaints');
            return err;
        });
};

export const getComplaintDetailsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_COMPLAINT_DETAILS_API.replace(':id', action.data.id);
    return axiosInstance.get(url, {
        params: clean({ society_id: action.data?.societyId }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load complaint');
            return err;
        });
};

export const createComplaintReq = (action) => {
    const url = apiEndpoints.SOCIETY_CREATE_COMPLAINT_API;
    const { onSuccess, onError, ...rest } = action.data || {};
    // Strip null/undefined/'' from the outgoing body so optional fields
    // like employee_id, flat_id, due_date, assigned_to_user_id never reach
    // the BE as literal null — the validator accepts null but the auto-assign
    // fallback (category default) only kicks in when the key is absent.
    const payload = clean(rest);
    return axiosInstance.post(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Complaint created');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to create complaint');
            return err;
        });
};

export const updateComplaintReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_COMPLAINT_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.put(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Complaint updated');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to update complaint');
            return err;
        });
};

export const deleteComplaintReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_COMPLAINT_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Complaint deleted');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to delete complaint');
            return err;
        });
};

export const changeComplaintStatusReq = (action) => {
    const url = apiEndpoints.SOCIETY_CHANGE_COMPLAINT_STATUS_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.patch(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Status updated');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to change status');
            return err;
        });
};

export const assignComplaintReq = (action) => {
    const url = apiEndpoints.SOCIETY_ASSIGN_COMPLAINT_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.patch(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Assignment updated');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to update assignment');
            return err;
        });
};

// A comment can carry up to 5 attachments in the same request. When files are
// present we must send multipart (field `files`, repeated once per file) and
// leave Content-Type unset so the browser adds the boundary itself. Text-only
// comments keep the plain JSON body the BE still accepts.
export const addComplaintCommentReq = (action) => {
    const url = apiEndpoints.SOCIETY_COMPLAINT_COMMENTS_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, files, ...payload } = action.data || {};

    let body = payload;
    if (Array.isArray(files) && files.length > 0) {
        const formData = new FormData();
        if (payload.comment_text) formData.append('comment_text', payload.comment_text);
        formData.append('is_internal', String(!!payload.is_internal));
        files.forEach((f) => formData.append('files', f));
        body = formData;
    }

    return axiosInstance.post(url, body)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Comment added');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to add comment');
            return err;
        });
};

export const uploadComplaintAttachmentsReq = (action) => {
    const url = apiEndpoints.SOCIETY_COMPLAINT_ATTACHMENTS_API.replace(':id', action.data.id);
    const files = action.data?.files || [];
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return axiosInstance.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
        .then(res => {
            if (res?.success) notify('success', res.message || 'Attachments uploaded');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to upload attachments');
            return err;
        });
};

export const deleteComplaintAttachmentReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_COMPLAINT_ATTACHMENT_API
        .replace(':id', action.data.id)
        .replace(':attachmentId', action.data.attachmentId);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Attachment removed');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to remove attachment');
            return err;
        });
};

export const getComplaintEnumsReq = () => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_COMPLAINT_ENUMS_API)
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load enums');
            return err;
        });
};

export const getComplaintStatsReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_COMPLAINT_STATS_API, {
        params: clean({ society_id: action.data?.societyId }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load stats');
            return err;
        });
};

// ---- Complaint Categories ----
export const getComplaintCategoriesReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_COMPLAINT_CATEGORIES_API, {
        params: clean({
            society_id: action.data?.societyId,
            search: action.data?.search,
            include_inactive: action.data?.includeInactive ? 'true' : undefined,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load categories');
            return err;
        });
};

export const getComplaintCategoriesMultiReq = (action) => {
    // Backend accepts JSON-array, csv, or repeated key. We use csv here — it
    // round-trips cleanly through axios' params serializer without needing a
    // custom paramsSerializer.
    const ids = Array.isArray(action.data?.societyIds)
        ? action.data.societyIds.filter(Boolean).join(',')
        : (action.data?.societyIds || '');
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_COMPLAINT_CATEGORIES_MULTI_API, {
        params: clean({
            society_ids: ids || undefined,
            search: action.data?.search,
            include_inactive: action.data?.includeInactive ? 'true' : undefined,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load categories');
            return err;
        });
};

export const bulkCreateComplaintCategoriesReq = (action) => {
    const { onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(apiEndpoints.SOCIETY_BULK_CREATE_COMPLAINT_CATEGORIES_API, payload)
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to bulk create categories');
            return err;
        });
};

export const createComplaintCategoryReq = (action) => {
    const { onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(apiEndpoints.SOCIETY_CREATE_COMPLAINT_CATEGORY_API, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Category created');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to create category');
            return err;
        });
};

export const updateComplaintCategoryReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_COMPLAINT_CATEGORY_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.put(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Category updated');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to update category');
            return err;
        });
};

export const deleteComplaintCategoryReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_COMPLAINT_CATEGORY_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Category deleted');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to delete category');
            return err;
        });
};

export const getComplaintAuditLogReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_COMPLAINT_AUDIT_LOG_API, {
        params: clean({
            society_id: action.data?.societyId,
            complaint_id: action.data?.complaintId,
            page: action.data?.page || 1,
            limit: action.data?.limit || 100,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load audit log');
            return err;
        });
};

export const rateComplaintReq = (action) => {
    const url = apiEndpoints.SOCIETY_RATE_COMPLAINT_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.patch(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Thanks for the feedback');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to submit rating');
            return err;
        });
};

// ---- Employee complaint history ----
export const getEmployeeComplaintsReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_EMPLOYEE_COMPLAINTS_API, {
        params: clean({
            society_id: action.data?.societyId,
            assigned_to_user_id: action.data?.userId,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load employee complaints');
            return err;
        });
};
